import React, { useMemo } from 'react';
import clsx from 'clsx';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { MATRIX_TIMELINE_EVENTS_STATUS } from '$app-web/components/chat/constants';
import { DateTimeFormats } from '$app-web/constants';
import ButtonBase from '$app-web/ui-kit/components/button-base';
import { appDateTime } from '$app-web/utils';

import { useMethods } from '../../../../../state/chat-client.state';
import { getEventMessage } from '../../../../../utils/chat-client.utils';
import classes from './styles.module.scss';

import { FontAwesomeIcon } from '$common/fontawesome-pro/react-fontawesome';

const MessageBubble = (props) => {
    const { chatUserData, event, isVoipMessage, onTitleClick, otherMembersLastReadMarker, title } = props;

    const { t } = useTranslation();

    const methods = useMethods();

    const dateTime = useMemo(() => {
        const messageDateTime = appDateTime(event.getTs());
        const todayDateTime = appDateTime();
        let dateTimeString = messageDateTime.format(DateTimeFormats.HourMinute);
        if (!messageDateTime.isSameDay(todayDateTime)) {
            if (messageDateTime.isSameYear(todayDateTime)) {
                dateTimeString = `${messageDateTime.format(DateTimeFormats.MonthShortNameWithDay)} ${dateTimeString}`;
            } else {
                dateTimeString = `${messageDateTime.format(DateTimeFormats.DateHumanReadableShortMonth)} ${dateTimeString}`;
            }
        }
        return dateTimeString;
    }, [event]);

    const messageStatus = useMemo(() => {
        if (chatUserData.userId === event.getSender()) {
            let iconName = 'clock';
            let iconType = 'fal';
            const iconBoxProps = {};
            if (
                event.status === MATRIX_TIMELINE_EVENTS_STATUS.COMPLETED ||
                event.status === MATRIX_TIMELINE_EVENTS_STATUS.SENT ||
                !event.status
            ) {
                if (event.getTs() <= otherMembersLastReadMarker?.event?.getTs?.()) {
                    iconName = 'check-double';
                } else {
                    iconName = 'check';
                }
            } else if (event.status === MATRIX_TIMELINE_EVENTS_STATUS.NOT_SENT) {
                iconName = 'exclamation-circle';
                iconType = 'fas';
                iconBoxProps.onClick = () => {
                    // TDDO: This won't re-render the component to get the new status of event, which is "sending".
                    methods.resendEvent(event);
                };
            }
            return (
                <div
                    className={clsx('flex inline items-center', {
                        [classes.notSentStatus]: event.status === MATRIX_TIMELINE_EVENTS_STATUS.NOT_SENT,
                    })}
                    {...iconBoxProps}
                >
                    <FontAwesomeIcon icon={[iconType, iconName]} className={classes.notSentStatusIcon} />
                    {event.status === MATRIX_TIMELINE_EVENTS_STATUS.NOT_SENT && (
                        <span className={clsx(classes.resendStatusText, 'typo-old weight-semi-bold')}>{t('resend')}</span>
                    )}
                </div>
            );
        }
        return '';
    }, [chatUserData.userId, event, methods, otherMembersLastReadMarker, t]);

    const messageBody = useMemo(() => {
        return getEventMessage(event, t);
    }, [event, t]);

    return (
        <div className={clsx('typo-old body1', classes.root)}>
            {isVoipMessage ? (
                <div className={clsx('typo-old', classes.messageBodyText)}>
                    {messageBody} {dateTime}
                </div>
            ) : (
                <>
                    {title.text && (
                        <ButtonBase className={clsx('typo-old body1')} onClick={onTitleClick} style={{ color: title.color }}>
                            {title.text}
                        </ButtonBase>
                    )}
                    <div>{messageBody}</div>
                    <div className={clsx('typo-old body3', classes.metaDataWrapper)}>
                        {dateTime} {messageStatus}
                    </div>
                </>
            )}
        </div>
    );
};

MessageBubble.defaultProps = {
    isVoipMessage: false,
    onTitleClick: () => {},
    otherMembersLastReadMarker: undefined,
    title: {},
};

MessageBubble.propTypes = {
    chatUserData: PropTypes.oneOfType([PropTypes.object]).isRequired,
    event: PropTypes.oneOfType([PropTypes.object]).isRequired,
    isVoipMessage: PropTypes.bool,
    onTitleClick: PropTypes.func,
    otherMembersLastReadMarker: PropTypes.oneOfType([PropTypes.object]),
    title: PropTypes.objectOf(PropTypes.string),
};

export default MessageBubble;
